import { Tooltip } from "@/components/TooltipV2";
import { CHALK_ADMIN_SURFACE_CLASS } from "@/components/admin/admin-visual-treatment";
import { PropsWithClassName } from "@/interfaces/with-classname";
import { useGlobalAppState } from "@/utils/app-state";
import { cx } from "@/utils/classnames";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { Placement, Strategy } from "@floating-ui/react-dom-interactions";
import React, { PropsWithChildren, ReactNode } from "react";

export { CHALK_ADMIN_SURFACE_CLASS };

export interface ChalkEmployeeGuardProps extends PropsWithClassName {
  ignoreAdminMode?: boolean;
  tooltipContent?: ReactNode;
  hideTooltip?: boolean;
  tooltipStrategy?: Strategy;
  fallback?: ReactNode;
  tooltipPlacement?: Placement;
}

/**
 * Only render `children` if the session user is a chalk employee.
 * (indicated by a user email that ends with `@chalk.ai`)
 * This is a spicy component and shouldn't be used very often.
 * Elements hidden by this component are still public and visible to customers
 * via the UI source code and should not be considered secure.
 */
export const ChalkEmployeeGuard: React.FC<PropsWithChildren<ChalkEmployeeGuardProps>> = ({
  children,
  tooltipContent,
  className,
  hideTooltip,
  tooltipStrategy,
  tooltipPlacement,
  ignoreAdminMode = false,
  fallback,
}) => {
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode });
  return isChalkEmployee && children ? (
    <ChalkAdminVisualWrapperRaw
      hideTooltip={hideTooltip}
      tooltipContent={tooltipContent}
      tooltipPlacement={tooltipPlacement}
      className={className}
      tooltipStrategy={tooltipStrategy}
    >
      {children}
    </ChalkAdminVisualWrapperRaw>
  ) : (
    fallback ?? null
  );
};

export interface ChalkAdminVisualWrapperProps extends PropsWithClassName {
  tooltipContent?: ReactNode;
  hideTooltip?: boolean;
  tooltipStrategy?: Strategy;
  tooltipPlacement?: Placement;
}

/**
 * If the user is a chalk employee, render the visually distinct wrapper around the children
 */
export const ChalkAdminVisualWrapper: React.FC<PropsWithChildren<ChalkAdminVisualWrapperProps>> = ({
  children,
  tooltipContent,
  className,
  tooltipStrategy,
  tooltipPlacement,
  hideTooltip = false,
}) => {
  const isChalkEmployee = useDANGEROUSisChalkEmployee();
  return isChalkEmployee ? (
    <ChalkAdminVisualWrapperRaw
      tooltipContent={tooltipContent}
      className={className}
      hideTooltip={hideTooltip}
      tooltipPlacement={tooltipPlacement}
      tooltipStrategy={tooltipStrategy}
    >
      {children}
    </ChalkAdminVisualWrapperRaw>
  ) : (
    children
  );
};

/**
 * The visually distinct wrapper for chalk admin components. This should not be used directly; we don't ever
 * want a non-chalk employee to see this wrapper. Instead, use the `ChalkAdminVisualWrapper`, which checks first.
 */
const ChalkAdminVisualWrapperRaw: React.FC<PropsWithChildren<ChalkAdminVisualWrapperProps>> = ({
  children,
  className,
  tooltipContent,
  tooltipStrategy,
  tooltipPlacement,
  hideTooltip = false,
}) => {
  // In demo mode the red surface is suppressed via CSS, so the explanatory tooltip
  // would point at nothing (and would leak "admin" into a customer demo) — hide it too.
  const {
    state: { demoMode },
  } = useGlobalAppState();
  const content = (
    <div className={cx(CHALK_ADMIN_SURFACE_CLASS, className)} data-chalk-demo-mode={demoMode}>
      {children}
    </div>
  );
  return hideTooltip || demoMode ? (
    content
  ) : (
    <Tooltip
      content={tooltipContent || "Chalk Admin Debug Component"}
      style={{ maxWidth: 800 }}
      strategy={tooltipStrategy}
      placement={tooltipPlacement}
      instant
    >
      {content}
    </Tooltip>
  );
};
